/**
 * BizCheckPage — C-2-3. 사업자 확인
 *
 * 업체명 검색 → 결과 목록(bc-list) → 아이템 선택(bc-result) → 소속 신청
 * 소속 신청 성공 → /app/welcome (B-2)
 * 취소 버튼 → PATCH /employees/request-affiliation/{roleId}
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import { Input, Button } from '@indipia/ui-core';
import { fetchJoinStatus, searchBiz, requestAffiliation } from '../api/employees';
import type { BizSearchItem } from '../api/employees';

export function BizCheckPage() {
  const navigate = useNavigate();

  // 이전 소속 정보 (join-status에서 companyName만)
  const { data: joinStatus } = useQuery({
    queryKey: ['employees', 'join-status'],
    queryFn: fetchJoinStatus,
    retry: 1,
  });

  const [keyword, setKeyword]         = useState('');
  const [results, setResults]         = useState<BizSearchItem[] | null>(null);
  const [selected, setSelected]       = useState<BizSearchItem | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  // 업체 검색
  const searchMutation = useMutation({
    mutationFn: () => searchBiz(keyword.trim()),
    onSuccess: (data) => {
      setResults(data);
      setSelected(null);
      setSearchError(null);
    },
    onError: () => {
      setSearchError('검색 중 오류가 발생했어요. 다시 시도해주세요.');
    },
  });

  // 소속 신청 — 성공 시 B-2 직원 웰컴 화면으로 이동
  const applyMutation = useMutation({
    mutationFn: () => requestAffiliation(selected!.compNo),
    onSuccess: () => {
      navigate('/app/welcome', { replace: true });
    },
  });

  const handleSelect = (item: BizSearchItem) => {
    setSelected(item);
    setResults(null);
    setKeyword(item.compName);
  };

  // 신청 폼 뷰
  return (
    <div className="flex h-full justify-center overflow-y-auto py-9">
      <div className="w-full max-w-[440px] px-6">

        <p className="mb-5 text-xl font-bold leading-snug text-text">
          새 소속 업체를<br />등록해주세요
        </p>

        {/* 이전 소속 정보 카드 — companyName 있을 때만 표시 */}
        {joinStatus?.companyName && (
          <div className="mb-5 rounded-xl border border-border bg-surface-raised px-4 py-3">
            <p className="mb-2 text-xs text-text-muted">이전 소속 정보</p>
            <div className="flex gap-3 text-sm">
              <span className="w-16 shrink-0 text-text-muted">이전 소속</span>
              <span className="font-medium text-text">{joinStatus.companyName}</span>
            </div>
          </div>
        )}

        {/* 업체 검색 */}
        <p className="mb-1.5 text-xs font-semibold text-text-muted">
          새 사업자명 <span className="text-group">*</span>
        </p>
        <div className="mb-2 flex gap-2">
          <Input
            className="field-auto flex-1"
            type="text"
            value={keyword}
            onChange={(e) => {
              setKeyword(e.target.value);
              setResults(null);
              setSelected(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && keyword.trim().length >= 2 && !searchMutation.isPending) {
                searchMutation.mutate();
              }
            }}
            placeholder="업체명을 입력하세요"
          />
          <Button
            type="button"
            disabled={keyword.trim().length < 2 || searchMutation.isPending}
            onClick={() => searchMutation.mutate()}
          >
            {searchMutation.isPending ? '검색 중' : '검색'}
          </Button>
        </div>

        {searchError && (
          <p className="mb-2 text-xs text-error">{searchError}</p>
        )}

        {/* bc-list — 검색 결과 */}
        {results !== null && (
          <div className="mb-3 flex flex-col gap-1.5">
            {results.length === 0 ? (
              <p className="py-3 text-center text-sm text-text-muted">검색 결과가 없어요</p>
            ) : (
              results.map((item) => (
                <button
                  key={item.compNo}
                  type="button"
                  onClick={() => handleSelect(item)}
                  className="flex w-full items-center justify-between rounded-xl border border-border bg-surface-raised px-4 py-3 text-left hover:border-group"
                >
                  <div>
                    <p className="text-sm font-bold text-text">{item.compName}</p>
                    <p className="mt-0.5 text-xs text-text-muted">대표 {item.compRepName}</p>
                  </div>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" className="shrink-0 text-text-muted">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              ))
            )}
          </div>
        )}

        {/* bc-result — 선택된 업체 */}
        {selected && (
          <div className="mb-5 rounded-xl border border-success/40 bg-success/5 px-4 py-3">
            <div className="mb-2 flex items-center gap-1.5">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-success">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <p className="text-xs font-bold text-success">소속 가능한 업체 확인됨</p>
            </div>
            <div className="flex gap-3 text-sm">
              <span className="w-10 shrink-0 text-text-muted">상호명</span>
              <span className="font-medium text-text">{selected.compName}</span>
            </div>
            <div className="mt-1 flex gap-3 text-sm">
              <span className="w-10 shrink-0 text-text-muted">대표자</span>
              <span className="font-medium text-text">{selected.compRepName}</span>
            </div>
          </div>
        )}

        {/* 소속 신청 버튼 */}
        <Button
          type="button"
          disabled={!selected || applyMutation.isPending}
          onClick={() => applyMutation.mutate()}
          className="w-full"
        >
          {applyMutation.isPending ? '신청 중...' : '이 업체로 소속 신청하기'}
        </Button>

        {applyMutation.isError && (
          <p className="mt-2 text-center text-xs text-error">
            소속 신청에 실패했어요. 다시 시도해주세요.
          </p>
        )}

        <button
          type="button"
          onClick={() => navigate('/app/no-company')}
          className="mt-4 w-full text-center text-sm text-text-muted underline underline-offset-2"
        >
          돌아가기
        </button>
      </div>
    </div>
  );
}
